import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@cockpitai/components/atoms'
import { InputDate } from '@cockpitai/components/atoms/InputDate'
import { Typography } from '@cockpitai/components/atoms/Typography'
import Modal, { TModalActionRef } from '@cockpitai/components/organisms/Modal'
import { zodResolver } from '@hookform/resolvers/zod'
import { RadioGroup } from '@headlessui/react'
import { Radio } from '@cockpitai/components/atoms/Radio'
import { Select } from '@cockpitai/components/atoms/Select'
import { allMonths, getIntervalOfYears } from '@cockpitai/utils/dateFormatter'
import { TValue } from './types'
import { isAfter, parseISO } from 'date-fns'

type TModalScheduleProps = {
  onChange(value: TValue): void
}

const schema = z
  .object({
    option: z.number(),
    endDate: z.optional(z.string()),
    endMonth: z.optional(
      z.object({
        title: z.string(),
        value: z.string(),
      }),
      z.undefined(),
    ),
    endYear: z.optional(
      z.object(
        {
          title: z.string(),
          value: z.string(),
        },
        { message: 'Campo obrigatório' },
      ),
      z.undefined(),
    ),
  })
  .refine(
    (form) => {
      if (form.option === 1 && !form.endDate) {
        return false
      }

      return true
    },
    {
      message: 'Campo obrigatório',
      path: ['endDate'],
    },
  )
  .refine(
    (form) => {
      if (form.option === 2 && !form.endMonth) {
        return false
      }

      return true
    },
    {
      message: 'Campo obrigatório',
      path: ['endMonth'],
    },
  )
  .refine(
    (form) => {
      if (form.option === 2 && !form.endYear) {
        return false
      }

      return true
    },
    {
      message: 'Campo obrigatório',
      path: ['endYear'],
    },
  )
  .refine(
    (schema) => {
      if (schema.endDate) {
        const parseDate = parseISO(schema.endDate)

        return isAfter(parseDate, new Date())
      }

      return true
    },
    {
      path: ['endDate'],
      message: 'A data deve ser maior que a data atual',
    },
  )

type TFormData = z.infer<typeof schema>

export type TModalTempFormRef = {
  open(value: any): void
  close(): void
}

export const ModalTempForm = forwardRef<TModalTempFormRef, TModalScheduleProps>(
  ({ onChange }, ref) => {
    const refModal = useRef<TModalActionRef>(null)

    const { control, handleSubmit, reset, resetField, watch, formState } =
      useForm<TFormData>({
        mode: 'all',
        defaultValues: {
          option: 1,
          endDate: '',
          endMonth: undefined,
          endYear: undefined,
        },
        resolver: zodResolver(schema),
      })

    useImperativeHandle(ref, () => ({
      open: (value: any) => {
        refModal.current?.open()

        if (value.endDate) {
          reset({
            option: 1,
            endDate: value.endDate,
          })
        } else if (value.endMonth) {
          const month = allMonths.find((item) => item.value === value.endMonth)

          reset({
            option: 2,
            endMonth: month,
            endYear: {
              title: value.endYear,
              value: value.endYear,
            },
          })
        }
      },
      close: () => {
        reset()
      },
    }))

    const optionSelected = watch('option')

    useEffect(() => {
      resetField('endDate')

      resetField('endMonth')

      resetField('endYear')
    }, [optionSelected, resetField])

    const onSubmit = handleSubmit((values) => {
      const { endDate, endMonth, endYear, option } = values

      if (option === 1 && endDate) {
        onChange({
          endDate,
          option,
        })

        refModal.current?.close()
      } else {
        onChange({
          endMonth: endMonth!.value,
          endYear: endYear!.value,
          option: 2,
        })

        refModal.current?.close()
      }
    })

    function handleCancel() {
      refModal.current?.close()

      reset()
    }

    return (
      <Modal ref={refModal} fullWidth={false} mode="normal">
        <div className="w-[33.75rem] p-6">
          <div className="flex items-center justify-between">
            <Typography type="subtitleTwo" className="text-gray-400">
              Agendar mensagem
            </Typography>
          </div>
          <div className="my-10 flex flex-col">
            <Controller
              control={control}
              name="option"
              render={({ field }) => {
                const optionSelected = field.value

                return (
                  <RadioGroup value={field.value} onChange={field.onChange}>
                    <div className="flex items-baseline">
                      <div className=" w-28">
                        <Radio value={1} label="Dia" />
                      </div>
                      <div className="flex-1">
                        <Controller
                          control={control}
                          name="endDate"
                          render={({ field, fieldState }) => (
                            <InputDate
                              placeholder="Selecionar o dia"
                              value={field.value}
                              isDisabled={optionSelected === 2}
                              onChange={field.onChange}
                              isInvalid={fieldState.invalid}
                              message={fieldState.error?.message}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex items-baseline">
                      <div className="w-28">
                        <Radio value={2} label="Mês e Ano" />
                      </div>
                      <div className="flex h-14 flex-1 gap-2">
                        <Controller
                          control={control}
                          name="endMonth"
                          render={({ field, fieldState }) => (
                            <Select
                              isDisabled={optionSelected === 1}
                              placeholder="Mês"
                              value={field.value}
                              onChange={field.onChange}
                              options={allMonths}
                              message={fieldState.error?.message}
                              isInvalid={fieldState.invalid}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="endYear"
                          render={({ field, fieldState }) => (
                            <Select
                              isDisabled={optionSelected === 1}
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Ano"
                              options={getIntervalOfYears()}
                              message={fieldState.error?.message}
                              isInvalid={fieldState.invalid}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </RadioGroup>
                )
              }}
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              onClick={handleCancel}
              colorScheme="primary"
              className="w-[5.5rem]">
              Cancelar
            </Button>
            <Button
              colorScheme="secondary"
              className="w-[5.5rem]"
              type="button"
              isDisabled={!formState.isValid}
              onClick={onSubmit}>
              Salvar
            </Button>
          </div>
        </div>
      </Modal>
    )
  },
)
